<template>
  <web-app-layout>
    <!-- Logo at top of content -->
    <div class="page-header">
      <img
        src="@/assets/images/logos/logo-text@2x.png"
        alt="Logo"
        class="header-logo"
      />
    </div>

    <div v-if="!isAuthenticated" class="auth-redirect">
      <SmartBanner />
    </div>

    <div class="profile-container">
      <div v-if="state.error" class="error-message">
        {{ state.error }}
      </div>

      <!-- Global Loading Indicator -->
      <div v-if="initialLoading" class="loading-container initial-loading">
        <el-icon :size="60" class="loading-spinner">
          <Loading />
        </el-icon>
        <p class="mt-3">Finding your organization profile...</p>
      </div>

      <template v-else>
        <!-- Organization Selector -->
        <div v-if="isAuthenticated && !isSlugRoute" class="org-selector-container">
          <label for="organization-select">Select Organization:</label>
          <el-select
            v-model="selectedOrgId"
            placeholder="Select organization"
            class="org-selector"
            @change="handleOrgChange"
            filterable
            remote
            :remote-method="searchOrganizations"
            :loading="searchLoading"
            clearable
            popper-class="org-select-dropdown"
            :teleported="false"
          >
            <el-option
              v-for="org in filteredOrganizations"
              :key="org.id"
              :label="org.name"
              :value="org.id"
            />
          </el-select>
        </div>

        <div class="profile-content-container">
          <div v-if="loading" class="loading-container">
            <el-icon :size="50" class="loading-spinner">
              <Loading />
            </el-icon>
            <p class="mt-3">Loading organization profile...</p>
          </div>

          <template v-else-if="state.organization">
            <!-- Background with Avatar and Name on top -->
            <div
              class="profile-header"
              :style="{
                backgroundImage: `url(${state.organization?.background || defaultBackground})`,
              }"
            >
              <div class="profile-content-overlay">
                <div class="profile-avatar">
                  <img :src="state.organization?.avatar || defaultAvatar" alt="Organization Avatar" />
                </div>
                <div class="profile-name-section">
                  <h1 class="user-name">{{ state.organization.name }}</h1>
                  <p class="user-title" v-if="state.organization.organization_type">
                    {{ formatOrgType }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Tabs Section - Only show if there are stickers -->
            <div v-if="state.stickers && state.stickers.length > 0" class="tabs-container">
              <div class="tab-buttons">
                <button
                  type="button"
                  :class="['tab-button', { active: activeTab === 'about' }]"
                  @click="activeTab = 'about'"
                >
                  ABOUT
                </button>
                <button
                  type="button"
                  :class="['tab-button', { active: activeTab === 'shop' }]"
                  @click="activeTab = 'shop'"
                >
                  SHOP
                </button>
              </div>

              <!-- About Tab Content -->
              <div v-if="activeTab === 'about'" class="tab-content">
                <div class="profile-info-section">
                  <div class="bio-section" v-if="state.organization?.purpose">
                    <h2 class="purpose-title">{{ state.organization.purpose }}</h2>
                  </div>

                  <div class="bio-section" v-if="state.organization?.about">
                    <p>{{ state.organization.about }}</p>
                  </div>

                  <!-- Values Section -->
                  <div class="values-section" v-if="state.organization?.values?.length">
                    <h3>VALUES</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="value in state.organization.values"
                        :key="value.id"
                        class="value-tag"
                      >
                        {{ value.value }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Bullhorns Section -->
                  <div class="values-section" v-if="state.organization?.bullhorns?.length">
                    <h3>CAUSES</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="bullhorn in state.organization.bullhorns"
                        :key="bullhorn.id"
                        class="interest-tag"
                      >
                        {{ bullhorn.bullhorn }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Channels Section -->
                  <div class="values-section" v-if="state.organization?.channels?.length">
                    <h3>CHANNELS</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="channel in state.organization.channels"
                        :key="channel.id"
                        class="channel-tag"
                      >
                        {{ channel.channel }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Contact Information -->
                  <div class="contact-section" v-if="state.organization.email">
                    <h3>CONTACT INFORMATION</h3>
                    <div class="contact-info">
                      <div class="info-item" v-if="state.organization?.email">
                        <i class="el-icon-message" />
                        <span>{{ state.organization.email }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Shop Tab Content -->
              <div v-if="activeTab === 'shop'" class="tab-content">
                <div class="profile-info-section">
                  <div class="stickers-section">
                    <h3>Organization Stickers</h3>
                    <div class="stickers-container">
                      <stickers-grid-template :stickers="state.stickers" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Tabs - Show About content directly when no stickers -->
            <div v-else class="profile-info-section">
              <div class="bio-section" v-if="state.organization?.purpose">
                <h2 class="purpose-title">{{ state.organization.purpose }}</h2>
              </div>

              <div class="bio-section" v-if="state.organization?.about">
                <p>{{ state.organization.about }}</p>
              </div>

              <!-- Values Section -->
              <div class="values-section" v-if="state.organization?.values?.length">
                <h3>VALUES</h3>
                <div class="tags-container">
                  <el-tag
                    v-for="value in state.organization.values"
                    :key="value.id"
                    class="value-tag"
                  >
                    {{ value.value }}
                  </el-tag>
                </div>
              </div>

              <!-- Bullhorns Section -->
              <div class="values-section" v-if="state.organization?.bullhorns?.length">
                <h3>CAUSES</h3>
                <div class="tags-container">
                  <el-tag
                    v-for="bullhorn in state.organization.bullhorns"
                    :key="bullhorn.id"
                    class="interest-tag"
                  >
                    {{ bullhorn.bullhorn }}
                  </el-tag>
                </div>
              </div>

              <!-- Channels Section -->
              <div class="values-section" v-if="state.organization?.channels?.length">
                <h3>CHANNELS</h3>
                <div class="tags-container">
                  <el-tag
                    v-for="channel in state.organization.channels"
                    :key="channel.id"
                    class="channel-tag"
                  >
                    {{ channel.channel }}
                  </el-tag>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </web-app-layout>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, computed, watch, ref,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import WebAppLayout from '@/views/web-app/dashboard/WebAppLayout.vue';
import gqlRequest from '@/views/web-app/dashboard/gql-request';
import defaultAvatarImg from '@/assets/images/default-profile.jpeg';
import defaultBackgroundImg from '@/assets/images/bg.jpg';
import SmartBanner from '@/views/web-app/sticker/SmartBanner.vue';
import StickersGridTemplate from '@/views/web-app/sticker/StickersGridTemplate.vue';

interface OrganizationData {
  id?: string;
  name?: string;
  avatar?: string;
  background?: string;
  about?: string;
  purpose?: string;
  email?: string;
  organization_type?: string;
  profit_type?: string;
  nonprofit_type?: string;
  dob?: string;
  onboarded?: boolean;
  active?: boolean;
  approved?: boolean;
  values?: Array<{
    id: string;
    value: string;
  }>;
  bullhorns?: Array<{
    id: string;
    bullhorn: string;
  }>;
  channels?: Array<{
    id: string;
    channel: string;
  }>;
  stickers?: Array<{
    id: string;
    name: string;
    imageUrl: string;
  }>;
}

export default defineComponent({
  name: 'OrganizationProfile',
  components: { WebAppLayout, SmartBanner, StickersGridTemplate },
  setup() {
    const {
      loading,
      fetchOrganization,
      fetchAllOrganizations,
      fetchPublicOrganization,
      fetchPublicOrganizationBySlug,
      fetchApprovedOrgStickers,
      fetchApprovedOrgStickersBySlug,
    } = gqlRequest();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const state = reactive({
      organization: null as OrganizationData | null,
      error: null as string | null,
      stickers: null as Array<{
        id: string;
        name: string;
        imageUrl: string;
      }> | null,
    });

    const organizations = ref<{ id: string, name: string, createdAt?: string }[]>([]);
    const filteredOrganizations = ref<{ id: string, name: string }[]>([]);
    const selectedOrgId = ref('');
    const searchLoading = ref(false);
    const initialLoading = ref(true);
    const activeTab = ref('about');

    const defaultAvatar = defaultAvatarImg;
    const defaultBackground = defaultBackgroundImg;

    const orgId = computed(() => route.params.id as string);
    const orgSlug = computed(() => route.params.slug as string);
    const isSlugRoute = computed(() => !!orgSlug.value);

    const isAuthenticated = computed(() => store.getters['users/isMeSignedIn']);

    const formatOrgType = computed(() => {
      if (!state.organization?.organization_type) return '';

      const type = state.organization.organization_type;

      return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    });

    function selectMostRecentOrganization() {
      if (!organizations.value.length) {
        return;
      }

      const firstOrg = organizations.value[0];
      if (firstOrg && firstOrg.id) {
        selectedOrgId.value = firstOrg.id;

        router.push(`/web/profile/organization/${firstOrg.id}`);
      }
    }

    function handleOrgChange(id: string) {
      if (!id) {
        if (organizations.value.length > 0) {
          selectMostRecentOrganization();
        }
        return;
      }

      if (id && id !== orgId.value) {
        router.push(`/web/profile/organization/${id}`);
      }
    }

    function searchOrganizations(query: string) {
      searchLoading.value = true;

      if (query === '') {
        filteredOrganizations.value = organizations.value;
        searchLoading.value = false;
        return;
      }

      const lowerQuery = query.toLowerCase();
      filteredOrganizations.value = organizations.value.filter((org) => org.name.toLowerCase().includes(lowerQuery));

      searchLoading.value = false;
    }

    async function fetchOrganizationStickers(organizationId: string) {
      try {
        let stickers;

        if (isSlugRoute.value) {
          stickers = await fetchApprovedOrgStickersBySlug(orgSlug.value);
        } else {
          stickers = await fetchApprovedOrgStickers(organizationId);
        }

        if (stickers && stickers.length > 0) {
          state.stickers = stickers;
        } else {
          state.stickers = [];
        }
      } catch (error) {
        console.error('Error fetching organization stickers:', error);
        state.stickers = [];
      }
    }

    async function fetchOrganizationProfile() {
      const identifier = orgId.value || orgSlug.value;
      if (!identifier) {
        return;
      }

      try {
        loading.value = true;
        state.error = null;
        let organization = null;

        if (isAuthenticated.value) {
          if (orgId.value) {
            organization = await fetchOrganization(orgId.value);
          } else {
            organization = await fetchPublicOrganizationBySlug(orgSlug.value);
          }
        } else if (isSlugRoute.value) {
          organization = await fetchPublicOrganizationBySlug(orgSlug.value);
        } else {
          organization = await fetchPublicOrganization(orgId.value);
        }

        if (organization) {
          state.organization = organization;
          initialLoading.value = false;

          await fetchOrganizationStickers(identifier);
        } else {
          state.error = 'Organization not found';
          console.error('Organization not found for identifier:', identifier);
        }
      } catch (error) {
        console.error('Error loading organization profile:', error);
        state.error = 'Failed to load organization data';
        if (error.message?.includes('not authorized')) {
          router.push('/auth/web/signin');
        }
      } finally {
        loading.value = false;
        initialLoading.value = false;
      }
    }

    async function loadOrganizations() {
      if (!isAuthenticated.value) return;

      try {
        searchLoading.value = true;
        initialLoading.value = true;
        const orgs = await fetchAllOrganizations();

        if (orgs && orgs.length) {
          organizations.value = orgs;
          filteredOrganizations.value = orgs;

          if (orgId.value) {
            selectedOrgId.value = orgId.value;
          } else {
            selectMostRecentOrganization();
          }
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        searchLoading.value = false;
        setTimeout(() => {
          initialLoading.value = false;
        }, 300);
      }
    }

    onMounted(async () => {
      if (isAuthenticated.value && !isSlugRoute.value) {
        await loadOrganizations();
      }

      const identifier = orgId.value || orgSlug.value;
      if (identifier) {
        fetchOrganizationProfile();
      }
    });

    watch(() => route.params.id, (newId) => {
      selectedOrgId.value = newId as string;

      if (newId) {
        fetchOrganizationProfile();
      } else if (organizations.value.length > 0 && !isSlugRoute.value) {
        selectMostRecentOrganization();
      }
    });

    watch(() => route.params.slug, (newSlug) => {
      if (newSlug) {
        fetchOrganizationProfile();
      }
    });

    watch(() => route.name, () => {
      const identifier = orgId.value || orgSlug.value;
      if (identifier) {
        fetchOrganizationProfile();
      }
    });

    return {
      state,
      loading,
      searchLoading,
      initialLoading,
      defaultAvatar,
      defaultBackground,
      isAuthenticated,
      formatOrgType,
      organizations,
      filteredOrganizations,
      selectedOrgId,
      orgSlug,
      isSlugRoute,
      activeTab,
      handleOrgChange,
      searchOrganizations,
    };
  },
});
</script>

<style lang="scss">
/* Global styles for Element Plus components */
.org-select-dropdown {
  .el-select-dropdown__item {
    padding-left: 0 !important;
  }
}
</style>

<style lang="scss" scoped>
/* Page header styles */
.page-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0 10px 0;
  margin-bottom: 20px;
}

.header-logo {
  height: 28px;
  width: auto;
  max-width: 150px;
  object-fit: contain;
}

.org-selector-container {
  margin-bottom: 25px;
  padding: 15px;
  background-color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  label {
    font-weight: 600;
    color: #2c3e50;
    min-width: 150px;
  }

  .org-selector {
    flex: 1;
    max-width: 500px;

    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      padding: 1px 30px 1px 0;
    }

    :deep(.el-input__inner) {
      padding-left: 0;
      margin-left: 0;
      text-indent: 0;
      transition: all 0.2s;

      &:hover, &:focus {
        border-color: #005C6B;
      }
    }

    :deep(.el-select__input) {
      margin-left: 0;
      padding-left: 0;
    }

    :deep(.el-select-v2__placeholder),
    :deep(.el-select__placeholder) {
      padding-left: 0;
    }

    :deep(.el-select__tags) {
      margin-left: 0;
      padding-left: 0;
    }
  }

  :deep(.el-select-dropdown__item) {
    padding-left: 16px !important;

    &.selected {
      color: #005C6B;
      font-weight: 500;
    }

    &:hover {
      background-color: #f0f9ff;
    }
  }
}

.profile-container {
  max-width: 900px;
  margin: 0;
  padding: 20px;
  text-align: left;
}

.profile-content-container {
  margin-top: 20px;
  text-align: left;
}

.profile-header {
  height: 300px;
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 40px;

  /* Removed the overlay */
}

.profile-content-overlay {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 30px;
  width: 100%;
  padding-top: 100px;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  overflow: hidden;
  background: #fff;
  box-shadow: none;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.profile-name-section {
  color: white;
  text-align: left;

  .user-name {
    color: white;
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 10px 0;
    letter-spacing: -0.5px;
  }

  .user-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    margin: 0;
    font-weight: 500;
  }
}

.tabs-container {
  margin-bottom: 30px;
  text-align: left;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0;
  }
}

.tab-buttons {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 30px;
  margin-left: 30px;
  position: relative;

  /* Mobile layout - remove horizontal margin */
  @media (max-width: 768px) {
    margin-left: 0;
  }
}

.tab-button {
  background: none;
  border: none;
  padding: 12px 0;
  margin-right: 24px;
  font-size: 14px;
  font-weight: 800;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-transform: uppercase;

  /* Custom indicator that matches text width */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: transparent;
    transition: background-color 0.3s ease;
  }

  &:hover {
    color: #1fbeaf;
  }

  &.active {
    color: #000000;

    &::after {
      background-color: #1fbeaf;
    }
  }
}

.tab-content {
  animation: fadeIn 0.3s ease-in;
  padding: 0 30px;

  /* Mobile layout - remove horizontal padding */
  @media (max-width: 768px) {
    padding: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.profile-info-section {
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }
}

.bio-section {
  text-align: left;
  margin: 30px 0;
  padding: 0;
  background: transparent;
  border-radius: 0;

  &:first-child {
    margin-top: 0;
  }

  p {
    color: #4a5568;
    line-height: 1.8;
    margin: 0;
    font-size: 18px;
  }
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 15px 0;
  padding: 5px;
}

.value-tag, .interest-tag, .channel-tag {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: transparent !important;
  color: #000000 !important;
  border: 1px solid #000000 !important;

  &:hover {
    transform: scale(1.05);
    background-color: #f5f5f5 !important;
  }
}

.values-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }
}

.contact-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 10px 0;
      
      i {
        color: #666;
      }
      
      span {
        color: #4a5568;
        font-size: 16px;
      }
    }
  }
}

.stickers-section {
  text-align: left;
  margin-top: 0;

  h3 {
    margin-top: 0;
  }
}

.stickers-container {
  margin-top: 15px;
}

.loading-spinner {
  animation: rotate 2s linear infinite;
  color: #1fbeaf;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 20px;

  p {
    color: #5c6c7c;
    font-size: 18px;
  }

  &.initial-loading {
    min-height: 50vh;
    background-color: #f8fafc;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

    .loading-spinner {
      color: #005C6B;
    }

    p {
      font-size: 20px;
      font-weight: 500;
    }
  }
}

.auth-redirect {
  text-align: center;
  padding: 60px;
  font-size: 20px;
  color: #5c6c7c;
  background: #f8fafc;
  border-radius: 12px;
  margin: 20px;
}

.error-message {
  background-color: #fef2f2;
  color: #ef4444;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 25px;
  text-align: center;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
}

/* Purpose title styling */
.purpose-title {
  color: #2c3e50;
  font-size: 24px;
  margin: 0 0 20px 0;
  text-align: left;
  font-weight: 700;
  line-height: 1.3;
}
</style>
